import { useCallback, useEffect, useState } from 'react';
import { api, downloadExcel, downloadQrPdf, fetchQrBlob, getApiPlatform } from '../api';
import type { EventInfo, EventStats, Platform, Question } from '../types';
import { QuestionsTab } from './QuestionsTab';
import { AccessTab } from './AccessTab';
import { formatEventDate, todayIsoDate } from '../utils/formatEventDate';

interface Props {
  isAdmin: boolean;
  demoPlatform?: Platform;
}

type Tab = 'events' | 'questions' | 'access';

function resolveUiPlatform(demoPlatform?: Platform): Platform {
  if (demoPlatform && demoPlatform !== 'dev') return demoPlatform;
  return getApiPlatform();
}

async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

function CopyLinkBox({
  label,
  url,
  onNotify,
}: {
  label: string;
  url: string;
  onNotify: (message: string, type: 'success' | 'error') => void;
}) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    const ok = await copyTextToClipboard(url);
    if (ok) {
      setCopied(true);
      onNotify('Ссылка скопирована', 'success');
      window.setTimeout(() => setCopied(false), 2000);
    } else {
      onNotify('Не удалось скопировать ссылку', 'error');
    }
  }

  return (
    <div className="link-box">
      <div className="link-box-row">
        <div className="link-label">{label}</div>
        <button type="button" className="btn-copy-link" onClick={handleCopy}>
          {copied ? 'Скопировано' : 'Копировать ссылку'}
        </button>
      </div>
      <div className="link-url">{url}</div>
    </div>
  );
}

interface EventCardProps {
  event: EventInfo;
  demoPlatform?: Platform;
  adminPassword?: string;
  onToggle: (event: EventInfo) => void;
  onExport: (event: EventInfo) => void;
  onDelete: (event: EventInfo) => void;
  onNotify: (message: string, type: 'success' | 'error') => void;
}

function EventCard({
  event,
  demoPlatform,
  adminPassword,
  onToggle,
  onExport,
  onDelete,
  onNotify,
}: EventCardProps) {
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(true);
  const [statsOpen, setStatsOpen] = useState(false);
  const [stats, setStats] = useState<EventStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    if (!event.isActive) {
      setQrUrl(null);
      setQrLoading(false);
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;

    setQrLoading(true);
    fetchQrBlob(event.id, adminPassword)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        setQrUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrUrl(null);
      })
      .finally(() => {
        if (!cancelled) setQrLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [event.id, event.isActive, adminPassword]);

  async function toggleStats() {
    if (statsOpen) {
      setStatsOpen(false);
      return;
    }

    setStatsOpen(true);
    if (stats) return;

    setStatsLoading(true);
    try {
      const data = await api.adminGetStats(event.id, adminPassword);
      setStats(data);
    } catch {
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }

  const uiPlatform = resolveUiPlatform(demoPlatform);

  async function handleDownloadPdf() {
    setPdfLoading(true);
    try {
      const msg = await downloadQrPdf(event.id, event.name, adminPassword);
      if (msg) {
        onNotify(msg, 'success');
      }
    } catch (e) {
      onNotify(e instanceof Error ? e.message : 'Не удалось скачать PDF', 'error');
    } finally {
      setPdfLoading(false);
    }
  }

  const eventDateLabel = formatEventDate(event.eventDate);

  return (
    <div className="event-item">
      <div className="event-item-header">
        <h3>
          {event.name}{' '}
          <span className={`badge ${event.isActive ? '' : 'inactive'}`}>
            {event.isActive ? 'активно' : 'закрыто'}
          </span>
        </h3>
        <button
          type="button"
          className="event-delete-btn"
          onClick={() => onDelete(event)}
          aria-label="Удалить мероприятие"
          title="Удалить мероприятие"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 7h16M10 11v6M14 11v6M6 7l1 12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"
            />
          </svg>
        </button>
      </div>
      <div className="event-meta">
        {eventDateLabel && <>Дата: {eventDateLabel} · </>}
        Анкет: {event.responsesCount ?? 0} · Код: {event.code}
      </div>

      {event.isActive ? (
        <>
          <div className="event-qr-block">
            {qrLoading && <div className="loading">QR…</div>}
            {qrUrl && (
              <>
                <img src={qrUrl} alt={`QR ${event.name}`} className="qr-preview" />
                <p className="subtitle event-qr-hint">Распечатайте QR-код на стойке регистрации</p>
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ marginTop: 12 }}
                  disabled={pdfLoading}
                  onClick={handleDownloadPdf}
                >
                  {pdfLoading
                    ? 'Отправка…'
                    : uiPlatform === 'telegram'
                      ? 'PDF в Telegram'
                      : uiPlatform === 'max'
                        ? 'PDF в MAX'
                        : 'PDF для печати'}
                </button>
              </>
            )}
          </div>

          {event.qrLink && (
            <CopyLinkBox label="Ссылка в QR-коде" url={event.qrLink} onNotify={onNotify} />
          )}
          {event.telegramStartLink && event.qrLink !== event.telegramStartLink && (
            <CopyLinkBox
              label="Ссылка через бота (start)"
              url={event.telegramStartLink}
              onNotify={onNotify}
            />
          )}
        </>
      ) : (
        <p className="subtitle event-closed-hint">Регистрация закрыта</p>
      )}

      <div className="btn-row">
        <button type="button" className="btn btn-secondary" onClick={toggleStats}>
          {statsOpen ? 'Скрыть статистику' : 'Статистика'}
        </button>
        <button type="button" className="btn btn-primary" onClick={() => onExport(event)}>
          {uiPlatform === 'telegram'
            ? 'Excel в Telegram'
            : uiPlatform === 'max'
              ? 'Excel в MAX'
              : 'Excel'}
        </button>
      </div>

      {statsOpen && (
        <div className="event-stats-inline">
          {statsLoading && <div className="loading">Загрузка…</div>}
          {stats && (
            <>
              <p>
                <strong>Всего анкет:</strong> {stats.totalResponses}
              </p>
              {Object.entries(stats.choiceBreakdown).map(([question, counts]) => (
                <div key={question} className="question-admin">
                  <strong>{question}</strong>
                  <table className="stats-table">
                    <tbody>
                      {Object.entries(counts).map(([answer, count]) => (
                        <tr key={answer}>
                          <td>{answer}</td>
                          <td>{count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      <button
        type="button"
        className="btn btn-secondary"
        style={{ marginTop: 8 }}
        onClick={() => onToggle(event)}
      >
        {event.isActive ? 'Закрыть регистрацию' : 'Открыть регистрацию'}
      </button>
    </div>
  );
}

export function AdminPage({ isAdmin, demoPlatform }: Props) {
  const [tab, setTab] = useState<Tab>('events');
  const [password, setPassword] = useState('');
  const [authed, setAuthed] = useState(isAdmin);
  const [events, setEvents] = useState<EventInfo[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [newEventName, setNewEventName] = useState('');
  const [newEventDate, setNewEventDate] = useState(todayIsoDate);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [questionSaving, setQuestionSaving] = useState(false);

  const adminPassword = authed && !isAdmin ? password : undefined;

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [ev, qs] = await Promise.all([
        api.adminGetEvents(adminPassword),
        api.adminGetQuestions(adminPassword),
      ]);
      setEvents(ev);
      setQuestions(qs);
      setAuthed(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка загрузки');
      if (!isAdmin) setAuthed(false);
    } finally {
      setLoading(false);
    }
  }, [adminPassword, isAdmin]);

  useEffect(() => {
    if (isAdmin) setAuthed(true);
  }, [isAdmin]);

  useEffect(() => {
    if (authed) loadData();
  }, [authed, loadData]);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    if (!password.trim()) {
      setError('Введите пароль');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const [ev, qs] = await Promise.all([
        api.adminGetEvents(password),
        api.adminGetQuestions(password),
      ]);
      setEvents(ev);
      setQuestions(qs);
      setAuthed(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Неверный пароль');
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  }

  function notify(message: string, type: 'success' | 'error') {
    if (type === 'success') {
      setSuccess(message);
      setError('');
    } else {
      setError(message);
      setSuccess('');
    }
  }

  async function createEvent() {
    if (!newEventName.trim()) return;
    if (!newEventDate) return;
    try {
      await api.adminCreateEvent(newEventName.trim(), newEventDate, adminPassword);
      setNewEventName('');
      setNewEventDate(todayIsoDate());
      await loadData();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка создания');
    }
  }

  async function toggleEvent(event: EventInfo) {
    try {
      await api.adminUpdateEvent(event.id, { isActive: !event.isActive }, adminPassword);
      await loadData();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка обновления');
    }
  }

  async function exportEvent(event: EventInfo) {
    try {
      const msg = await downloadExcel(event.id, event.name, adminPassword);
      if (msg) {
        notify(msg, 'success');
      }
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка выгрузки', 'error');
    }
  }

  async function deleteEvent(event: EventInfo) {
    const responses = event.responsesCount ?? 0;
    const warning =
      responses > 0
        ? `Удалить мероприятие «${event.name}»?\n\nБудут удалены ${responses} анкет. Это действие нельзя отменить.`
        : `Удалить мероприятие «${event.name}»?\n\nЭто действие нельзя отменить.`;

    if (!window.confirm(warning)) return;

    setError('');
    try {
      await api.adminDeleteEvent(event.id, adminPassword);
      await loadData();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка удаления');
    }
  }

  async function saveQuestion(
    data: {
      text: string;
      type: Question['type'];
      options: string[];
      isRequired: boolean;
    },
    id?: number
  ) {
    setQuestionSaving(true);
    setError('');
    try {
      if (id) {
        await api.adminUpdateQuestion(id, data, adminPassword);
      } else {
        await api.adminCreateQuestion(data, adminPassword);
      }
      await loadData();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка сохранения');
      throw e;
    } finally {
      setQuestionSaving(false);
    }
  }

  async function hideQuestion(id: number) {
    setError('');
    try {
      await api.adminDeleteQuestion(id, adminPassword);
      await loadData();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка');
    }
  }

  async function restoreQuestion(id: number) {
    setError('');
    try {
      await api.adminUpdateQuestion(id, { isActive: true }, adminPassword);
      await loadData();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка');
    }
  }

  if (!authed) {
    return (
      <div className="admin-login card">
        <h2>Админ-панель</h2>
        <p className="subtitle">Введите пароль сотрудника клиники</p>
        {demoPlatform === 'telegram' && (
          <p className="subtitle">
            Войдите паролем или попросите супер-админа добавить ваш ID через вкладку «Доступ». Узнать ID: /myid в боте.
          </p>
        )}
        {demoPlatform === 'max' && (
          <p className="subtitle">
            Войдите паролем или попросите супер-админа добавить ваш MAX ID через вкладку «Доступ». Узнать ID: /myid в боте.
          </p>
        )}
        {demoPlatform === 'dev' && (
          <p className="subtitle">Локальный режим: пароль из ADMIN_PASSWORD в .env</p>
        )}
        {error && <div className="error">{error}</div>}
        <form onSubmit={handleLogin}>
          <div className="field">
            <input
              type="password"
              placeholder="Пароль"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <button type="submit" className="btn btn-primary">
            Войти
          </button>
        </form>
      </div>
    );
  }

  return (
    <div>
      <div className="header">
        <div className="logo">Админ · Авиценна</div>
        <h1 className="title">Управление</h1>
        <p className="subtitle">Мероприятия, вопросы анкеты и выгрузка данных</p>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div className="tabs">
        <button
          type="button"
          className={`tab ${tab === 'events' ? 'active' : ''}`}
          onClick={() => setTab('events')}
        >
          Мероприятия
        </button>
        <button
          type="button"
          className={`tab ${tab === 'questions' ? 'active' : ''}`}
          onClick={() => setTab('questions')}
        >
          Вопросы
        </button>
        <button
          type="button"
          className={`tab ${tab === 'access' ? 'active' : ''}`}
          onClick={() => setTab('access')}
        >
          Доступ
        </button>
      </div>

      {loading && <div className="loading">Загрузка…</div>}

      {tab === 'events' && !loading && (
        <>
          <div className="card">
            <h2>Новое мероприятие</h2>
            <div className="field">
              <label>Название мероприятия</label>
              <input
                placeholder="Например: День открытых дверей"
                value={newEventName}
                onChange={(e) => setNewEventName(e.target.value)}
              />
            </div>
            <div className="field">
              <label>Дата проведения</label>
              <input
                type="date"
                value={newEventDate}
                onChange={(e) => setNewEventDate(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={createEvent}
              disabled={!newEventName.trim() || !newEventDate}
            >
              Создать мероприятие
            </button>
          </div>

          {events.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              demoPlatform={demoPlatform}
              adminPassword={adminPassword}
              onToggle={toggleEvent}
              onExport={exportEvent}
              onDelete={deleteEvent}
              onNotify={notify}
            />
          ))}
        </>
      )}

      {tab === 'questions' && !loading && (
        <QuestionsTab
          questions={questions}
          saving={questionSaving}
          onCreate={(data) => saveQuestion(data)}
          onUpdate={(id, data) => saveQuestion(data, id)}
          onHide={hideQuestion}
          onRestore={restoreQuestion}
        />
      )}

      {tab === 'access' && (
        <AccessTab adminPassword={adminPassword} onNotify={notify} />
      )}
    </div>
  );
}
